import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Users, 
  Trash2, 
  Shield,
  CheckCircle,
  AlertCircle,
  Mail,
  Calendar,
  Plus,
  Briefcase,
  DollarSign
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function TeamsPage({ currentUser }) {
  const { t } = useLanguage();
  const [superadmins, setSuperadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Add member dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addType, setAddType] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  
  useEffect(() => {
    fetchTeams();
  }, []);
  
  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/teams`, {
        withCredentials: true
      });
      setSuperadmins(response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      showMessage('error', 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/superadmin/teams/${userToDelete.user_id}`, {
        withCredentials: true
      });
      showMessage('success', `${userToDelete.name} has been removed`);
      fetchTeams();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete member');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddError('');
    
    if (!addType) {
      setAddError('Please select a member type');
      return;
    }
    if (addPassword.length < 6) {
      setAddError('Password must be at least 6 characters');
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(`${API}/superadmin/teams/add-member`, {
        name: addName,
        email: addEmail,
        password: addPassword,
        member_type: addType,
      }, { withCredentials: true });
      showMessage('success', `${addType.charAt(0).toUpperCase() + addType.slice(1)} member added successfully`);
      setAddDialogOpen(false);
      setAddName('');
      setAddEmail('');
      setAddPassword('');
      setAddType('');
      fetchTeams();
    } catch (error) {
      setAddError(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getInitials = (name) => {
    if (!name) return 'SA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getMemberTypeBadge = (memberType) => {
    if (!memberType || memberType === 'superadmin') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
          <Shield className="w-3 h-3" />
          Super Admin
        </span>
      );
    }
    if (memberType === 'operations') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
          <Briefcase className="w-3 h-3" />
          Operations
        </span>
      );
    }
    if (memberType === 'finance') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
          <DollarSign className="w-3 h-3" />
          Finance
        </span>
      );
    }
    return null;
  };

  const isFullSuperadmin = !currentUser?.member_type || currentUser?.member_type === 'superadmin';
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t("nav.teams")}</h2>
          <p className="text-gray-600 mt-1">Manage super admin accounts and team members</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-lg">
            <Shield className="w-5 h-5 text-indigo-600" />
            <span className="text-indigo-700 font-medium">{superadmins.length} Member{superadmins.length !== 1 ? 's' : ''}</span>
          </div>
          {isFullSuperadmin && (
            <Button
              onClick={() => setAddDialogOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          )}
        </div>
      </div>
      
      {/* Status Message */}
      {message.text && (
        <div className={`
          flex items-center gap-2 p-4 rounded-lg
          ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : ''}
          ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : ''}
        `}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}
      
      {/* Teams Table */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-gray-50">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Team Members
          </CardTitle>
          <CardDescription>All users with admin panel access</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">User</TableHead>
                <TableHead className="font-semibold">{t("common.email")}</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="font-semibold">{t("common.status")}</TableHead>
                <TableHead className="text-right font-semibold">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {superadmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    No team members found
                  </TableCell>
                </TableRow>
              ) : (
                superadmins.map((admin) => {
                  const isCurrentUser = currentUser?.user_id === admin.user_id;
                  
                  return (
                    <TableRow key={admin.user_id} data-testid={`admin-row-${admin.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={admin.picture} alt={admin.name} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-700">
                              {getInitials(admin.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">{admin.name}</p>
                            {isCurrentUser && (
                              <span className="text-xs text-indigo-600 font-medium">(You)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          {admin.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getMemberTypeBadge(admin.member_type)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(admin.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {isCurrentUser ? (
                          <span className="text-xs text-gray-400 italic">Cannot delete self</span>
                        ) : !isFullSuperadmin ? (
                          <span className="text-xs text-gray-400 italic">-</span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(admin)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-admin-${admin.user_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? 
              This action cannot be undone and will remove all access for this user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Create a new team member with restricted access to the admin panel.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member-name">Full Name</Label>
              <Input
                id="member-name"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="John Doe"
                required
                data-testid="add-member-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-email">Email</Label>
              <Input
                id="member-email"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="member@company.com"
                required
                data-testid="add-member-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-password">Password</Label>
              <Input
                id="member-password"
                type="password"
                value={addPassword}
                onChange={(e) => setAddPassword(e.target.value)}
                placeholder="Min 6 characters"
                minLength={6}
                required
                data-testid="add-member-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Member Type</Label>
              <Select value={addType} onValueChange={setAddType}>
                <SelectTrigger data-testid="add-member-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operations">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-amber-600" />
                      Operations
                    </div>
                  </SelectItem>
                  <SelectItem value="finance">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      Finance
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {addType === 'operations' && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Access: Dashboard, Companies (edit, backdoor login), Reports
                </p>
              )}
              {addType === 'finance' && (
                <p className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                  Access: Dashboard, Companies (edit, add balance), Payment History, Reports
                </p>
              )}
            </div>
            {addError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="add-member-error">
                {addError}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={adding} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-member-submit">
                {adding ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TeamsPage;
